'use client';

export const dynamic = 'force-dynamic';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from '@/shared/ui/shadcn/input';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/ui/shadcn/card';
import { Label } from '@/shared/ui/shadcn/label';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { authService } from '@/features/auth';
import { ApiClientError } from '@/shared/lib/api';
import { Loader2, CheckCircle } from 'lucide-react';
import { Logo } from '@/shared/ui/brand/Logo';

interface PageProps {
  params: Promise<Record<string, never>>;
  searchParams: Promise<Record<string, never>>;
}

function createForgotPasswordSchema(
  tValid: ReturnType<typeof useTranslations>
) {
  return z.object({
    email: z
      .string()
      .min(1, tValid('required'))
      .email(tValid('email')),
  });
}

type ForgotPasswordFormData = z.infer<
  ReturnType<typeof createForgotPasswordSchema>
>;

export default function ForgotPasswordPage(_props: PageProps) {
  const t = useTranslations('auth.forgotPassword');
  const tValid = useTranslations('common.errors.validation');
  const [success, setSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const schema = useMemo(() => createForgotPasswordSchema(tValid), [tValid]);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      await authService.forgotPassword({ email: data.email });
      setSubmittedEmail(data.email);
      setSuccess(true);
    } catch (error) {
      if (error instanceof ApiClientError) {
        const fieldErrors = error.getFieldErrors();
        if (fieldErrors.email) {
          setError('email', { message: fieldErrors.email });
        } else {
          setError('root', {
            message: error.getFirstError() || t('errorGeneric'),
          });
        }
      } else {
        setError('root', { message: t('errorGeneric') });
      }
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <Logo />
            </div>
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-12 w-12 text-primary" />
            </div>
            <CardTitle className="text-screen-title text-center">
              {t('successTitle')}
            </CardTitle>
            <CardDescription className="text-center">
              {t('successDescription', { email: submittedEmail })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert className="border-primary/20 bg-primary/5">
              <AlertDescription className="text-center">
                <p className="font-medium">{t('successMessage')}</p>
                <p className="font-medium">{t('successExpiry')}</p>
              </AlertDescription>
            </Alert>
          </CardContent>
          <CardFooter className="flex justify-center">
            <Link
              href="/auth/login"
              className="font-medium text-primary hover:text-primary/80"
            >
              {t('backToLogin')}
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <Logo />
          </div>
          <CardTitle className="text-screen-title text-center">{t('title')}</CardTitle>
          <CardDescription className="text-center">
            {t('description')}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                {...register('email')}
                placeholder={t('emailPlaceholder')}
                className={errors.email ? 'border-destructive' : ''}
              />
              {errors.email && (
                <p className="text-form-error text-destructive">{errors.email.message}</p>
              )}
            </div>

            {errors.root?.message && (
              <Alert variant="destructive">
                <AlertDescription>{errors.root.message}</AlertDescription>
              </Alert>
            )}
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" disabled={isSubmitting} className="w-full">
              {isSubmitting ? (
                <>
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                  {t('sending')}
                </>
              ) : (
                t('submit')
              )}
            </Button>
            <Link
              href="/auth/login"
              className="text-form-label text-primary hover:text-primary/80"
            >
              {t('backToLogin')}
            </Link>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
